import { createApi } from '@reduxjs/toolkit/query/react';

import { authQuery } from './baseQuery';
import type { PaginatedResponse } from 'src/types/common';
import type {
  IServiceLine,
  IServiceLineEvent,
  ICreateServiceLine,
  IUpdateServiceLine,
} from 'src/types/serviceLines';
import { convertObjToUrl } from 'src/utils/server-side-pagination';
import type { IPaginationState } from 'src/utils/server-side-pagination';

export const serviceLineAPISlice = createApi({
  reducerPath: 'presentation-service-service-line-api',
  tagTypes: ['ServiceLines'],
  baseQuery: (args: any, api: any, extraOptions: any) =>
    authQuery(
      args,
      api,
      (extraOptions = { ...extraOptions, baseUrl: window.RUNTIME_CONFIG.PRESENTATION_SERVICE })
    ),
  endpoints: (builder) => ({
    getServiceLines: builder.query<
      PaginatedResponse<IServiceLine>,
      { state: IPaginationState; limit: number }
    >({
      query: ({ state, limit }) => `/scheduled-pauses?limit=${limit}${convertObjToUrl(state)}`,
      providesTags: ['ServiceLines'],
    }),

    getServiceLineById: builder.query<IServiceLine, { id: string }>({
      query: ({ id }) => `/scheduled-pauses/${id}`,
      providesTags: ['ServiceLines'],
    }),

    getScheduledPausesByServiceLineId: builder.query<IServiceLine[], { serviceLineId: string }>({
      query: ({ serviceLineId }) => `/scheduled-pauses?serviceLineId=${serviceLineId}`,
      providesTags: ['ServiceLines'],
    }),

    getScheduledPauseEventsByServiceLineId: builder.query<
      IServiceLineEvent[],
      { serviceLineId: string; pauseId?: string }
    >({
      query: ({ serviceLineId, pauseId }) =>
        `/scheduled-pauses/events?serviceLineId=${serviceLineId}${pauseId ? `&pauseId=${pauseId}` : ''}`,
      providesTags: ['ServiceLines'],
    }),

    create: builder.mutation<IServiceLine, Partial<ICreateServiceLine>>({
      query: (requestData: Partial<ICreateServiceLine>) => ({
        url: `/scheduled-pauses`,
        method: 'POST',
        body: requestData,
      }),
      invalidatesTags: ['ServiceLines'],
    }),

    update: builder.mutation<IServiceLine, { id: string; requestData: IUpdateServiceLine }>({
      query: ({ id, requestData }) => ({
        url: `/scheduled-pauses/${id}`,
        method: 'PATCH',
        body: requestData,
      }),
      invalidatesTags: ['ServiceLines'],
    }),

    cancel: builder.mutation<IServiceLine, { id: string; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/scheduled-pauses/${id}/cancel`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: ['ServiceLines'],
    }),
  }),
});

export const {
  useCancelMutation,
  useCreateMutation,
  useGetScheduledPauseEventsByServiceLineIdQuery,
  useGetScheduledPausesByServiceLineIdQuery,
  useGetServiceLineByIdQuery,
  useGetServiceLinesQuery,
  useUpdateMutation,
} = serviceLineAPISlice;
